/**
 * IB-2 — the install route that is ALWAYS there when the app is not installed.
 *
 * WHY THIS EXISTS. `PWAInstallPrompt` was the only way to install, and it is an *interruption*:
 * it decides on its own when to appear, and three separate things stop it —
 *
 *   1. the 14-day dismissal snooze in localStorage. **Uninstalling the PWA does not clear it.**
 *      Tap × once, uninstall the app a week later, come back on a genuinely clean device, and the
 *      banner still refuses to show. That is almost certainly what the founder hit.
 *   2. the desktop gate — viewport ≥ 1024px renders null, so a laptop never offers install at all.
 *   3. Chrome's own cooldown after an uninstall or a dismissed native dialog, during which
 *      `beforeinstallprompt` simply does not fire. The banner's 10s manual fallback covers this,
 *      but only if (1) and (2) let it get that far.
 *
 * None of those are bugs on their own — a banner that re-pesters on every refresh is worse. The gap
 * is that there was no *deliberate* way to install: nothing a person could go and press when they
 * had decided they wanted the app. This is that. It never nags; it is simply always available.
 *
 * Uses the real one-tap prompt when the browser has offered one, and honest per-platform
 * instructions when it has not — never a button that does nothing.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Download, Share, Plus, X } from 'lucide-react';
import { isStandalone, isIos, showInstallPrompt, onInstallPromptAvailable, canInstall } from '../../utils/pwa';

interface Props {
  /**
   * 'link' for footers (muted text), 'button' for menus (a real tappable row), and
   * [JOB B S5X item 5] 'header' for the compact, permanent pill that now sits in every portal header.
   */
  variant?: 'link' | 'button' | 'header';
  style?: React.CSSProperties;
}

export function InstallAppButton({ variant = 'link', style }: Props) {
  // Installed apps must never be told to install. Checked once at mount and again on `appinstalled`.
  const [installed, setInstalled] = useState(() => isStandalone());
  const [oneTap, setOneTap] = useState(() => canInstall());
  const [showHow, setShowHow] = useState(false);

  useEffect(() => {
    const onInstalled = () => setInstalled(true);
    window.addEventListener('appinstalled', onInstalled);
    // Fires immediately if the event was already captured at module load (ONBOARD-FIX-1 A2.2).
    const unsub = onInstallPromptAvailable(() => setOneTap(true));
    return () => {
      window.removeEventListener('appinstalled', onInstalled);
      unsub();
    };
  }, []);

  if (installed) return null;

  const handle = async () => {
    const outcome = await showInstallPrompt();
    // 'unavailable' = the browser never offered a prompt, or it was already spent. Show the real
    // steps rather than leaving a dead button — the ONBOARD-FIX-1 lesson.
    if (outcome === 'unavailable') setShowHow(true);
    else if (outcome === 'accepted') setInstalled(true);
  };

  /*
   * [JOB B S5X item 5] THE SHEET IS PORTALLED TO <body>, AND IN A HEADER THAT IS NOT OPTIONAL.
   *
   * Every shell header here is `position: sticky` with a backdrop filter, and the mobile ones sit in a
   * 56px-tall bar. A `position: fixed` overlay rendered INSIDE one is contained by it — a filter or a
   * transform makes the header the containing block for fixed descendants (CSS Transforms §3), which
   * is the exact mechanism that hid the birthday card's whole action row at BDAY-RING-FIX. The sheet
   * therefore leaves the header's subtree entirely, as the occasion viewer does.
   */
  const howSheet = showHow ? createPortal(
    <div
      role="dialog"
      aria-label="How to install"
      style={{
        position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
      onClick={() => setShowHow(false)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)', width: '100%', maxWidth: 460,
          borderRadius: '18px 18px 0 0', padding: '18px 20px 26px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <strong style={{ fontSize: 15, color: 'var(--t1)' }}>Install Aisistoo</strong>
          <button type="button" onClick={() => setShowHow(false)} aria-label="Close"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--t3)' }}>
            <X size={18} />
          </button>
        </div>
        {isIos() ? (
          <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, color: 'var(--t2)', lineHeight: 1.75 }}>
            <li>Tap <Share size={13} style={{ verticalAlign: -2 }} /> <strong>Share</strong> at the bottom of Safari</li>
            <li>Scroll down and tap <Plus size={13} style={{ verticalAlign: -2 }} /> <strong>Add to Home Screen</strong></li>
            <li>Tap <strong>Add</strong></li>
          </ol>
        ) : (
          <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, color: 'var(--t2)', lineHeight: 1.75 }}>
            <li>Open the browser menu (<strong>⋮</strong>, top right)</li>
            <li>Tap <strong>Install app</strong> — or <strong>Add to Home screen</strong></li>
            <li>Confirm <strong>Install</strong></li>
          </ol>
        )}
        {/*
          Said plainly rather than hidden: after an uninstall, Chrome stops offering the one-tap
          prompt for a while. The menu route always works, and a person who came here on purpose
          deserves to know why the quick way is missing instead of assuming the app is broken.
        */}
        {!oneTap && (
          <p style={{ margin: '14px 0 0', fontSize: 11.5, color: 'var(--t4)', lineHeight: 1.6 }}>
            Your browser isn’t offering the one-tap install right now — that’s normal for a while
            after uninstalling. The menu steps above always work.
          </p>
        )}
      </div>
    </div>,
    document.body,
  ) : null;

  if (variant === 'header') {
    return (
      <>
        <button
          type="button"
          onClick={handle}
          data-install-header=""
          title="Install Aisistoo on this device"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0,
            height: 32, padding: '0 10px', borderRadius: 999, cursor: 'pointer',
            border: '1px solid var(--border)', background: 'var(--surface)',
            fontSize: 12, fontWeight: 700, color: 'var(--t2)', fontFamily: 'var(--font-body)',
            whiteSpace: 'nowrap', ...style,
          }}
        >
          <Download size={14} color="var(--p)" />
          {/*
            The word is hidden below 380px rather than the button: a mobile header already carries a
            title, a bell and an avatar, and an icon-only pill keeps the route available on the
            narrowest phones instead of pushing something else off the bar. The accessible name stays.
          */}
          <span className="hidden min-[380px]:inline">Install app</span>
          <span className="sr-only min-[380px]:hidden">Install app</span>
        </button>
        {howSheet}
      </>
    );
  }

  return (
    <>
      {variant === 'link' ? (
        <button
          type="button"
          onClick={handle}
          style={{
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            font: 'inherit', fontSize: 12, fontWeight: 600, color: 'var(--t3)',
            display: 'inline-flex', alignItems: 'center', gap: 5, ...style,
          }}
        >
          <Download size={13} /> Install app
        </button>
      ) : (
        <button
          type="button"
          onClick={handle}
          style={{
            display: 'flex', alignItems: 'center', gap: 10, width: '100%',
            padding: '12px 14px', borderRadius: 12, cursor: 'pointer',
            border: '1px solid var(--border)', background: 'var(--surface)',
            fontSize: 14, fontWeight: 700, color: 'var(--t1)', ...style,
          }}
        >
          <Download size={17} color="var(--p)" /> Install Aisistoo
        </button>
      )}

      {/*
        ONE sheet, shared by all three variants — it moved up into `howSheet` when it was portalled.
        A copy per variant is how two spellings of the same instructions start to drift.
      */}
      {howSheet}
    </>
  );
}

export default InstallAppButton;
